import React, { useState, useEffect } from 'react';
import { Building2, Globe, Calendar, Check, Loader2, AlertCircle } from 'lucide-react';
import { useSettings, useUpdateGeneralSettings } from '../../hooks';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

const timezones = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const dateFormats = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
];

const languages = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
];

export default function GeneralSettings() {
  const { data: settings, isLoading } = useSettings();
  const updateSettings = useUpdateGeneralSettings();

  const [formData, setFormData] = useState({
    company_name: '',
    timezone: 'America/New_York',
    date_format: 'MM/DD/YYYY',
    language: 'en',
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Initialize form from settings
  useEffect(() => {
    if (settings?.general) {
      setFormData({
        company_name: settings.general.company_name?.value || settings.general.company_name || '',
        timezone: settings.general.timezone?.value || 'America/New_York',
        date_format: settings.general.date_format?.value || 'MM/DD/YYYY',
        language: settings.general.language?.value || 'en',
      });
    }
  }, [settings]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateSettings.mutateAsync(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveError(error.message || 'Failed to save settings. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" className="h-5 w-40" />
        </div>
        <Skeleton variant="text" className="h-4 w-72" />
        <div className="space-y-5">
          <div className="space-y-2">
            <Skeleton variant="text" className="h-4 w-24" />
            <Skeleton variant="rectangular" className="h-[42px] w-full max-w-[400px]" />
          </div>
          <div className="space-y-2">
            <Skeleton variant="text" className="h-4 w-20" />
            <Skeleton variant="rectangular" className="h-[42px] w-full max-w-[400px]" />
          </div>
          <div className="space-y-2">
            <Skeleton variant="text" className="h-4 w-24" />
            <Skeleton variant="rectangular" className="h-[42px] w-full max-w-[400px]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-grey-900 dark:text-grey-50 m-0 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-teal-500" />
          General Settings
        </h2>
        <p className="text-sm text-grey-500 dark:text-grey-400 mt-2">
          Configure your organization's basic information and preferences.
        </p>
      </div>

      {/* Form */}
      <div className="space-y-5">
        {/* Company Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-grey-900 dark:text-grey-50">
            Company Name
          </label>
          <Input
            type="text"
            className="max-w-[400px]"
            value={formData.company_name}
            onChange={(e) => handleChange('company_name', e.target.value)}
            placeholder="Enter your company name"
          />
          <p className="text-xs text-grey-500">This name appears in reports and communications.</p>
        </div>

        {/* Timezone */}
        <div className="space-y-2">
          <label className="flex items-center gap-1.5 text-sm font-medium text-grey-900 dark:text-grey-50">
            <Globe className="w-3.5 h-3.5" />
            Timezone
          </label>
          <Select value={formData.timezone} onValueChange={(val) => handleChange('timezone', val)}>
            <SelectTrigger className="max-w-[400px]">
              <SelectValue placeholder="Select timezone" />
            </SelectTrigger>
            <SelectContent>
              {timezones.map((tz) => (
                <SelectItem key={tz.value} value={tz.value}>
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-grey-500">All timestamps will be displayed in this timezone.</p>
        </div>

        {/* Date Format */}
        <div className="space-y-2">
          <label className="flex items-center gap-1.5 text-sm font-medium text-grey-900 dark:text-grey-50">
            <Calendar className="w-3.5 h-3.5" />
            Date Format
          </label>
          <Select value={formData.date_format} onValueChange={(val) => handleChange('date_format', val)}>
            <SelectTrigger className="max-w-[400px]">
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              {dateFormats.map((df) => (
                <SelectItem key={df.value} value={df.value}>
                  {df.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Language */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-grey-900 dark:text-grey-50">
            Language
          </label>
          <Select value={formData.language} onValueChange={(val) => handleChange('language', val)}>
            <SelectTrigger className="max-w-[400px]">
              <SelectValue placeholder="Select language" />
            </SelectTrigger>
            <SelectContent>
              {languages.map((lang) => (
                <SelectItem key={lang.value} value={lang.value}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-grey-500">Interface language for all users in your organization.</p>
        </div>
      </div>

      {/* Error Message */}
      {saveError && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 max-w-[400px]">
          <AlertCircle className="w-[18px] h-[18px] shrink-0" />
          <span>{saveError}</span>
        </div>
      )}

      {/* Save Button */}
      <Button
        variant={saveSuccess ? 'success' : 'default'}
        size="lg"
        onClick={handleSave}
        disabled={updateSettings.isPending}
      >
        {updateSettings.isPending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Saving...
          </>
        ) : saveSuccess ? (
          <>
            <Check className="w-4 h-4" />
            Saved!
          </>
        ) : (
          'Save Changes'
        )}
      </Button>
    </div>
  );
}
